import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import {
    LEGAL_MEASURE_TYPE_OPTIONS,
    LegalMeasureStatus,
    LegalMeasureType,
    type LegalMeasure,
    type SelectOption,
} from '../types/LegalCase';
import type { ErrorField } from '../types/ErrorField';

interface AddMeasureValue {
    measureType: LegalMeasureType;
    status: LegalMeasureStatus;
    requestedAt: string;
    description: string;
}

// Gerencia as medidas judiciais de um caso: lista, adiciona, muda status e remove. Fetch
// próprio pelo id do caso (a seção atualiza sem recarregar o page-view inteiro). Mesmo
// padrão do useLegalCaseEvents (seededCaseRef re-seed por caso, opções vindas do page-form).
export default function useLegalCaseMeasures(caseId: string | null, initialMeasures?: LegalMeasure[]) {
    const [measures, setMeasures] = useState<LegalMeasure[]>(initialMeasures || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);
    const [typeOptions, setTypeOptions] = useState<SelectOption[]>(LEGAL_MEASURE_TYPE_OPTIONS);
    const seededCaseRef = useRef<string | null>(null);
    // Geração de request: guarda contra resposta obsoleta. O fetch do mount pode chegar depois
    // de uma mutação e sobrescrever a lista com o snapshot antigo. Cada fetch captura a geração
    // ao iniciar e só aplica o resultado se ainda for a corrente (o próprio ++ invalida os em voo).
    const fetchGenRef = useRef(0);

    const fetchMeasures = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getMeasures(caseId);
            if (fetchGenRef.current === generation) setMeasures(result.items || []);
        } catch {
            // silencioso — a seção já mostra as medidas que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setMeasures(initialMeasures || []);
        setErrorFields([]);
        // Semeia com o page-view (paint imediato) e re-busca do servidor. O re-fetch cobre a
        // remontagem da seção (ao alternar de aba): sem ele, voltaria ao `initialMeasures` do
        // page-view — carregado antes de qualquer inclusão — e o registro recém-criado
        // "sumiria" até recarregar a página. initialMeasures fora das deps de propósito.
        fetchMeasures();
        // initialMeasures fora das deps de propósito (referência nova a cada render).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    useEffect(() => {
        const loadTypes = async () => {
            try {
                const opts = await recoveryApi.getLegalCaseFormOptions();
                if (opts.legalMeasureTypes?.length) setTypeOptions(opts.legalMeasureTypes);
            } catch {
                // fallback local já preenche
            }
        };
        loadTypes();
    }, []);

    const clearErrors = () => setErrorFields([]);

    const addMeasure = async (data: AddMeasureValue): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        setErrorFields([]);
        try {
            await recoveryApi.addMeasure(caseId, {
                measureType: data.measureType,
                status: data.status || LegalMeasureStatus.REQUESTED,
                requestedAt: data.requestedAt || undefined,
                description: data.description || undefined,
            });
            await fetchMeasures();
            toast.success('Medida registrada');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos. Verifique os campos.');
                setErrorFields(e.data.errors);
                return false;
            }
            if (e instanceof ApiError && e.status === 404) {
                toast.error(e.data?.message || 'Caso não encontrado');
                return false;
            }
            toast.error(e?.message || 'Erro ao registrar medida');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (measureId: string, status: LegalMeasureStatus) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.updateMeasureStatus(caseId, measureId, status);
            await fetchMeasures();
            toast.success('Status atualizado');
        } catch (e: any) {
            // 422 = transição inválida; o backend explica no message.
            toast.error(e?.data?.message || e?.message || 'Erro ao atualizar status');
        } finally {
            setIsSaving(false);
        }
    };

    const removeMeasure = async (measureId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeMeasure(caseId, measureId);
            await fetchMeasures();
            toast.success('Medida removida');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover medida');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        measures, isLoading, isSaving, errorFields, typeOptions,
        addMeasure, changeStatus, removeMeasure, clearErrors,
    };
}

export type { AddMeasureValue };
